import React, { useState, useEffect, useRef } from 'react';
import type { Drill } from '../types';
import { Plus, Trash2, Search, Star } from 'lucide-react';
import { POSITION_GROUPS } from '../utils/sportUtils';
import { toast } from 'react-hot-toast';
import { createYoutubeSearchUrl } from '../utils/videoUtils';
import SuperModalPortal from './SuperModalPortal';
import { FavoriteService, FavoriteDrillStep } from '../services/favoriteService';

// Import the useTeamStore to get the current team and sport
import { useTeamStore } from '../store/teamStore';

interface StepFormState {
  id?: string;  // ID from the database, may be undefined for new steps
  name: string;
  setup: string;
  execution: string;
  equipment: string;
  video_url: string;
  coaching_points: string[];
  description: string;
}

interface PositionGroupFormState {
  group_name: string;
  steps: StepFormState[];
}

interface DrillFormState {
  name: string;
  duration: string;
  description: string;
  focus_area: string;
  intensity_level: string;
  position_groups: PositionGroupFormState[];
}

interface DrillFormProps {
  onClose: () => void;
  onSave: (drillData: Partial<Drill>) => void;
  editingDrill?: Drill | null;
  selectedPositionGroup?: string;
}

const DrillForm: React.FC<DrillFormProps> = ({
  onClose,
  onSave,
  editingDrill = null,
  selectedPositionGroup = 'ALL'
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  // Add refs to track newly created coaching point inputs
  const newCoachingPointRefs = useRef<{[key: string]: React.RefObject<HTMLInputElement>}>({});
  
  // Get the current team from the team store
  const { currentTeam } = useTeamStore();
  
  // Get all position groups for the current sport
  const getSportPositionGroups = () => {
    // If no team or no sport, return just ALL
    if (!currentTeam?.sport) return ['ALL'];
    
    // Get position groups for the current sport from the POSITION_GROUPS object
    const sportGroups = POSITION_GROUPS[currentTeam.sport] || [];
    
    // Always include ALL as the first option
    return ['ALL', ...sportGroups];
  };
  
  const [availablePositionGroups, setAvailablePositionGroups] = useState<string[]>(getSportPositionGroups());
  const [favoriteSteps, setFavoriteSteps] = useState<FavoriteDrillStep[]>([]);
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(false);
  const [favoriteStepIds, setFavoriteStepIds] = useState<Set<string>>(new Set());
  
  // Update available position groups when the sport changes
  useEffect(() => {
    setAvailablePositionGroups(getSportPositionGroups());
  }, [currentTeam?.sport]);

  // Load favorite drill steps when component mounts
  useEffect(() => {
    loadFavoriteSteps();
  }, []);

  // Debug: Log current state
  useEffect(() => {
    console.log('DrillForm state:', {
      favoriteSteps: favoriteSteps.length,
      isLoadingFavorites,
      currentTeam: currentTeam?.sport
    });
  }, [favoriteSteps, isLoadingFavorites, currentTeam]);

  const loadFavoriteSteps = async () => {
    setIsLoadingFavorites(true);
    try {
      const userId = await FavoriteService.getCurrentUserId();
      console.log('Loading favorites for user:', userId);
      const favorites = await FavoriteService.getFavoriteDrillSteps(userId);
      console.log('Loaded favorites:', favorites);
      setFavoriteSteps(favorites);
      
      // Extract step IDs that are already favorited
      const favoriteIds = new Set(favorites.map(f => f.id));
      setFavoriteStepIds(favoriteIds);
    } catch (error) {
      console.error('Error loading favorite steps:', error);
      toast.error('Failed to load favorite steps');
    } finally {
      setIsLoadingFavorites(false);
    }
  };

  const handleSelectFavoriteStep = (favoriteStep: FavoriteDrillStep, positionGroupIndex: number, stepIndex: number) => {
    const newStep = {
      name: favoriteStep.step_name,
      setup: favoriteStep.setup || '',
      execution: favoriteStep.execution || '',
      equipment: favoriteStep.equipment || '',
      video_url: favoriteStep.video_url || '',
      coaching_points: [...(favoriteStep.coaching_points || [])],
      description: ''
    };

    setDrillForm(prev => ({
      ...prev,
      position_groups: prev.position_groups.map((group, index) => {
        if (index === positionGroupIndex) {
          const currentStep = group.steps[stepIndex];
          const isCurrentStepEmpty = !currentStep.name.trim() && !currentStep.setup.trim() && !currentStep.execution.trim();
          
          if (isCurrentStepEmpty) {
            // Replace the empty step
            const updatedSteps = [...group.steps];
            updatedSteps[stepIndex] = newStep;
            return {
              ...group,
              steps: updatedSteps
            };
          } else {
            // Add a new step after the current one
            const updatedSteps = [...group.steps];
            updatedSteps.splice(stepIndex + 1, 0, newStep);
            return {
              ...group,
              steps: updatedSteps
            };
          }
        }
        return group;
      })
    }));

         const action = !drillForm.position_groups[positionGroupIndex].steps[stepIndex].name.trim() ? 'replaced' : 'added';
     toast.success(`"${favoriteStep.step_name}" ${action} from favorites`);
     
     // If the step has an ID and is now in the form, mark it as favorited
     if (favoriteStep.id) {
       setFavoriteStepIds(prev => new Set([...prev, favoriteStep.id]));
     }
  };

  const [drillForm, setDrillForm] = useState<DrillFormState>(() => {
    if (editingDrill) {
      // When editing, show all position groups if no specific group is selected
      const filteredPositionGroups = selectedPositionGroup === 'ALL'
        ? editingDrill.position_groups
        : editingDrill.position_groups.filter(group => group.group_name === selectedPositionGroup);

      return {
        name: editingDrill.name,
        duration: editingDrill.duration?.toString() || '',
        description: editingDrill.description,
        focus_area: editingDrill.focus_area,
        intensity_level: (editingDrill as any).intensity_level || 'Walk-through',
        position_groups: filteredPositionGroups.map(group => ({
          group_name: group.group_name,
          steps: group.steps.map(step => {
            // Handle both coaching_points and coachingPoints property names
            const points = [];
            if (step.coaching_points && Array.isArray(step.coaching_points)) {
              points.push(...step.coaching_points.map(p => typeof p === 'string' ? p : ((p as any).point || '')));
            }
            // Access coachingPoints with type assertion since it might not be in the type definition
            if ((step as any).coachingPoints && Array.isArray((step as any).coachingPoints)) {
              points.push(...(step as any).coachingPoints.map((p: any) => typeof p === 'string' ? p : (p.point || '')));
            }
            
            return {
              id: step.id,  // Preserve the step ID if it exists
              name: step.name || '',
              setup: step.setup || '',
              execution: step.execution || '',
              coaching_points: points.length > 0 ? points : [],
              equipment: step.equipment || '',
              video_url: step.video_url || '',
              description: step.description || ''
            };
          })
        }))
      };
    }
    // For new drills, start with the selected position group
    return {
      name: '',
      duration: '',
      description: '',
      focus_area: '',
      intensity_level: 'Walk-through',
      position_groups: [{
        group_name: selectedPositionGroup,
        steps: [{
          name: '',
          setup: '',
          execution: '',
          coaching_points: [],
          equipment: '',
          video_url: '',
          description: ''
        }]
      }]
    };
  });

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Update the useEffect for editing drill changes
  useEffect(() => {
    if (editingDrill) {
      // Filter position groups based on selected position group
      const filteredPositionGroups = selectedPositionGroup === 'ALL'
        ? editingDrill.position_groups
        : editingDrill.position_groups.filter(pg => pg.group_name === selectedPositionGroup);

      // Map the filtered position groups to form state
      const formattedPositionGroups = filteredPositionGroups.map(pg => ({
        group_name: pg.group_name,
        steps: pg.steps.map(step => {
          // Handle both coaching_points and coachingPoints property names
          const points = [];
          if (step.coaching_points && Array.isArray(step.coaching_points)) {
            points.push(...step.coaching_points.map(p => typeof p === 'string' ? p : ((p as any).point || '')));
          }
          // Access coachingPoints with type assertion since it might not be in the type definition
          if ((step as any).coachingPoints && Array.isArray((step as any).coachingPoints)) {
            points.push(...(step as any).coachingPoints.map((p: any) => typeof p === 'string' ? p : (p.point || '')));
          }
          
          return {
            id: step.id,  // Preserve the step ID if it exists
            name: step.name || '',
            setup: step.setup || '',
            execution: step.execution || '',
            coaching_points: points.length > 0 ? points : [],
            equipment: step.equipment || '',
            video_url: step.video_url || '',
            description: step.description || ''
          };
        })
      }));

      setDrillForm({
        name: editingDrill.name || '',
        duration: editingDrill.duration?.toString() || '',
        description: editingDrill.description || '',
        focus_area: editingDrill.focus_area || '',
        intensity_level: (editingDrill as any).intensity_level || 'Walk-through',
        position_groups: formattedPositionGroups
      });
    }
  }, [editingDrill, selectedPositionGroup]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate drillForm, especially duration
    if (!drillForm.name.trim()) {
      toast.error('Drill name is required');
      return;
    }
    
    // Validate duration to ensure it's a valid number
    const parsedDuration = parseInt(drillForm.duration);
    if (isNaN(parsedDuration) || parsedDuration <= 0) {
      toast.error('Duration must be a positive number');
      return;
    }
    
    // First, ensure all steps have video URLs by generating them if missing
    const positionGroupsWithVideos = drillForm.position_groups.map(group => ({
      ...group,
      steps: group.steps.map(step => {
        // If step doesn't have a video URL, generate one
        if (!step.video_url && step.name && currentTeam?.sport) {
          const videoUrl = createYoutubeSearchUrl(
            step.name,
            currentTeam.sport,
            group.group_name
          );
          console.log(`Auto-generated video URL for ${step.name}: ${videoUrl}`);
          return { ...step, video_url: videoUrl };
        }
        return step;
      })
    }));
    
    // Format the drill data with video URLs included, ensuring step IDs are preserved
    const drillData = {
      id: editingDrill?.id, // Important: Include the ID if editing
      name: drillForm.name,
      duration: parsedDuration,
      description: drillForm.description,
      focus_area: drillForm.focus_area,
      intensity_level: drillForm.intensity_level as 'Walk-through' | 'Technique focused' | 'Game speed',
      position_groups: positionGroupsWithVideos.map(group => ({
        group_name: group.group_name,
        steps: group.steps.map(step => ({
          // Important: Always include the step ID if it exists
          id: step.id, // Preserve the step ID
          name: step.name,
          setup: step.setup,
          execution: step.execution,
          coaching_points: step.coaching_points,
          equipment: step.equipment || '',
          video_url: step.video_url || '', // Include generated video URLs
          description: step.description || ''
        }))
      }))
    };

    // Log the drill data with IDs before saving
    console.log(`${editingDrill ? 'Updating' : 'Creating'} drill with data:`, JSON.stringify(drillData, null, 2));

    // Debug console log to track drill saving
    console.log(`Saving drill ${drillData.name} with duration: ${drillData.duration}`);
    
    onSave(drillData);
    
    // Close the form after saving
    onClose();
  };

  const handlePositionChange = (position: string) => {
    setDrillForm(prev => {
      const existingGroup = prev.position_groups.find(pg => pg.group_name === position);
      
      if (existingGroup) {
        // If the position group exists, remove it (toggle off)
        // But prevent removing ALL position groups - at least one must remain
        if (prev.position_groups.length <= 1) {
          // Don't allow removing the last position group
          toast.error('At least one position group is required', {
            id: 'min-position-group',
            duration: 2000
          });
          return prev;
        }
        
        return {
          ...prev,
          position_groups: prev.position_groups.filter(pg => pg.group_name !== position)
        };
      } else {
        // Add the position group if it doesn't exist (toggle on)
        return {
          ...prev,
          position_groups: [...prev.position_groups, {
            group_name: position,
            steps: [{
              name: '',
              setup: '',
              execution: '',
              coaching_points: [],
              equipment: '',
              video_url: '',
              description: ''
            }]
          }]
        };
      }
    });
  };

  const addStep = (groupIndex: number) => {
    setDrillForm(prev => ({
      ...prev,
      position_groups: prev.position_groups.map((group, index) => {
        if (index === groupIndex) {
          return {
            ...group,
            steps: [
              ...group.steps,
              {
                name: '',
                setup: '',
                execution: '',
                coaching_points: [],
                equipment: '',
                video_url: '',
                description: ''
              }
            ]
          };
        }
        return group;
      })
    }));
  };

  const removeStep = (groupIndex: number, stepIndex: number) => {
    setDrillForm(prev => ({
      ...prev,
      position_groups: prev.position_groups.map((group, index) => {
        if (index === groupIndex) {
          return {
            ...group,
            steps: group.steps.filter((_, i) => i !== stepIndex)
          };
        }
        return group;
      })
    }));
  };

  const updateStep = (positionGroup: string, stepIndex: number, field: string, value: string) => {
    setDrillForm(prev => ({
      ...prev,
      position_groups: prev.position_groups.map(pg => {
        if (pg.group_name === positionGroup) {
          return {
            ...pg,
            steps: pg.steps.map((step, index) => {
              if (index === stepIndex) {
                return { ...step, [field]: value };
              }
              return step;
            })
          };
        }
        return pg;
      })
    }));
  };

  const addCoachingPoint = (positionGroupIndex: number, stepIndex: number) => {
    const updatedPositionGroups = [...drillForm.position_groups];
    updatedPositionGroups[positionGroupIndex].steps[stepIndex].coaching_points.push('');
    setDrillForm(prev => ({
      ...prev,
      position_groups: updatedPositionGroups
    }));
    
    // Create a unique key for this coaching point
    const pointIndex = updatedPositionGroups[positionGroupIndex].steps[stepIndex].coaching_points.length - 1;
    const refKey = `${positionGroupIndex}-${stepIndex}-${pointIndex}`;
    
    // Create a ref for the new input
    newCoachingPointRefs.current[refKey] = React.createRef();
    
    // Focus on the new input after render
    setTimeout(() => {
      if (newCoachingPointRefs.current[refKey]?.current) {
        newCoachingPointRefs.current[refKey].current?.focus();
      }
    }, 0);
  };

  const removeCoachingPoint = (positionGroupIndex: number, stepIndex: number, pointIndex: number) => {
    const updatedPositionGroups = [...drillForm.position_groups];
    updatedPositionGroups[positionGroupIndex].steps[stepIndex].coaching_points.splice(pointIndex, 1);
    setDrillForm(prev => ({
      ...prev,
      position_groups: updatedPositionGroups
    }));
  };

  const updateCoachingPoint = (positionGroup: string, stepIndex: number, pointIndex: number, value: string) => {
    setDrillForm(prev => ({
      ...prev,
      position_groups: prev.position_groups.map(pg => {
        if (pg.group_name === positionGroup) {
          return {
            ...pg,
            steps: pg.steps.map((step, index) => {
              if (index === stepIndex) {
                return {
                  ...step,
                  coaching_points: step.coaching_points.map((point, idx) => {
                    if (idx === pointIndex) {
                      // Allow empty values - don't force placeholder text
                      return value;
                    }
                    return point;
                  })
                };
              }
              return step;
            })
          };
        }
        return pg;
      })
    }));
  };

  // Replace handleFindVideo with handleGenerateVideoUrl
  const handleGenerateVideoUrl = (positionGroup: string, stepIndex: number) => {
    try {
      // Get the current step data
      const step = drillForm.position_groups
        .find(pg => pg.group_name === positionGroup)?.steps[stepIndex];
      
      if (!step) {
        console.error('Step not found for', { positionGroup, stepIndex });
        return;
      }
      
      // Make sure we have a sport
      if (!currentTeam?.sport) {
        console.error('Team sport is not defined');
        toast.error("Team sport is not defined");
        return;
      }
      
      // Create a YouTube search URL
      const videoUrl = createYoutubeSearchUrl(
        step.name,
        currentTeam.sport,
        positionGroup
      );
      
      console.log('Generated video URL:', videoUrl);
      
      // Update the step with the video URL
      const updatedPositionGroups = [...drillForm.position_groups];
      const groupIndex = updatedPositionGroups.findIndex(pg => pg.group_name === positionGroup);
      
      if (groupIndex !== -1) {
        updatedPositionGroups[groupIndex] = {
          ...updatedPositionGroups[groupIndex],
          steps: updatedPositionGroups[groupIndex].steps.map((s, idx) => 
            idx === stepIndex ? { ...s, video_url: videoUrl } : s
          )
        };
        
        setDrillForm({
          ...drillForm,
          position_groups: updatedPositionGroups
        });
      }
      
      // Show success toast
      toast.success("Video URL generated", {
        duration: 2000
      });
      
      // Open the video URL in a new tab
      window.open(videoUrl, '_blank');
    } catch (error: any) {
      console.error('Error generating video URL:', error);
      toast.error(`Error generating video URL: ${error.message || 'Unknown error'}`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 pb-20 md:pb-4 z-[100]">
      <div
        ref={modalRef}
        className="bg-white rounded-lg w-full max-w-4xl flex flex-col max-h-[75vh] md:max-h-[85vh]"
      >
        <div className="p-3 flex-shrink-0 border-b">
          <h3 className="text-lg font-bold">
            {editingDrill ? 'Edit Drill' : 'Add Drill'}
          </h3>
        </div>
        <div className="overflow-y-auto px-3 py-2" style={{ maxHeight: 'calc(85vh - 120px)' }}>
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Basic Drill Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="drill-name">Drill Name</label>
                <input
                  id="drill-name"
                  type="text"
                  name="name"
                  aria-label="Drill Name"
                  value={drillForm.name}
                  onChange={(e) => setDrillForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="drill-duration">Duration (minutes)</label>
                <input
                  id="drill-duration"
                  type="number"
                  name="duration"
                  aria-label="Duration"
                  value={drillForm.duration}
                  onChange={(e) => setDrillForm(prev => ({ ...prev, duration: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="drill-intensity">Intensity Level</label>
                <select
                  id="drill-intensity"
                  aria-label="Intensity Level"
                  value={drillForm.intensity_level}
                  onChange={(e) => setDrillForm(prev => ({ ...prev, intensity_level: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="Walk-through">Walk-through</option>
                  <option value="Technique focused">Technique focused</option>
                  <option value="Game speed">Game speed</option>
                </select>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                name="description"
                value={drillForm.description}
                onChange={(e) => setDrillForm(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md h-20"
                required
              ></textarea>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Focus Area</label>
              <input
                type="text"
                name="focus_area"
                value={drillForm.focus_area}
                onChange={(e) => setDrillForm(prev => ({ ...prev, focus_area: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>

            {/* Position Groups */}
            <div className="mt-5 mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">Position Groups</label>
              <p className="text-sm text-gray-500 mb-2">
                Select the position groups this drill is intended for. Select "ALL" if the drill applies to all positions.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {availablePositionGroups.map((position) => {
                  const isSelected = drillForm.position_groups.some(pg => pg.group_name === position);
                  return (
                    <div
                      key={position}
                      onClick={() => handlePositionChange(position)}
                      className={`cursor-pointer px-3 py-2 rounded-md text-center flex items-center justify-center gap-2 ${
                        isSelected
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {position}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Steps for each position group */}
            {drillForm.position_groups.map((positionGroup, positionGroupIndex) => (
              <div key={positionGroup.group_name} className="border rounded-lg p-4 bg-gray-50 mb-4">
                <h4 className="text-lg font-medium mb-4">Steps for {positionGroup.group_name}</h4>
                
                                 {positionGroup.steps.map((step, stepIndex) => (
                   <div key={stepIndex} className="border rounded-lg p-4 bg-white mb-4">
                     <div className="mb-2">
                       {/* Step header with action buttons */}
                       <div className="flex justify-between items-center mb-2">
                         <span className="font-medium">Step {stepIndex + 1}</span>
                         
                         <div className="flex gap-2">
                           {/* Save as Favorite button */}
                           <button
                             type="button"
                             onClick={async () => {
                               try {
                                 const userId = await FavoriteService.getCurrentUserId();
                                 const stepId = step.id || 'temp-' + Date.now();
                                 const isFavorite = await FavoriteService.toggleFavoriteStep(userId, stepId);
                                 
                                 // Update local state immediately for better UX
                                 if (isFavorite) {
                                   setFavoriteStepIds(prev => new Set([...prev, stepId]));
                                   toast.success(`"${step.name}" added to favorites`);
                                 } else {
                                   setFavoriteStepIds(prev => {
                                     const newSet = new Set(prev);
                                     newSet.delete(stepId);
                                     return newSet;
                                   });
                                   toast.success(`"${step.name}" removed from favorites`);
                                 }
                                 
                                 // Refresh favorites list
                                 await loadFavoriteSteps();
                               } catch (error) {
                                 console.error('Error toggling favorite:', error);
                                 toast.error('Failed to update favorites');
                               }
                             }}
                             className={`btn-sm flex items-center gap-1 ${
                               step.id && favoriteStepIds.has(step.id) 
                                 ? 'bg-yellow-100 text-yellow-800 border-yellow-300 hover:bg-yellow-200' 
                                 : 'btn-secondary'
                             }`}
                             title={step.id && favoriteStepIds.has(step.id) ? "Remove from favorites" : "Save this step to favorites"}
                           >
                             <Star 
                               size={16} 
                               className={step.id && favoriteStepIds.has(step.id) ? "text-yellow-500 fill-yellow-500" : ""}
                             />
                             {step.id && favoriteStepIds.has(step.id) ? "Favorited" : "Favorite"}
                           </button>
                           
                           {/* Individual step remove button */}
                           <button
                             type="button"
                             onClick={() => removeStep(positionGroupIndex, stepIndex)}
                             className="btn-danger btn-sm flex items-center gap-1"
                             disabled={positionGroup.steps.length <= 1}
                             title={positionGroup.steps.length <= 1 ? "Cannot remove the only step" : "Remove this step"}
                           >
                             <Trash2 size={16} />
                             Remove
                           </button>
                         </div>
                       </div>
                       
                       {/* Favorites Dropdown - positioned below step header to avoid layout conflicts */}
                       <div className="mb-2">
                         {isLoadingFavorites ? (
                           <div className="px-2 py-1 border border-gray-300 rounded text-xs bg-gray-100 text-gray-500">
                             Loading...
                           </div>
                         ) : favoriteSteps.length > 0 ? (
                           <select
                             onChange={(e) => {
                               if (e.target.value) {
                                 const selectedFavorite = favoriteSteps.find(f => f.id === e.target.value);
                                 if (selectedFavorite) {
                                   handleSelectFavoriteStep(selectedFavorite, positionGroupIndex, stepIndex);
                                 }
                                 e.target.value = ''; // Reset selection
                               }
                             }}
                             className="px-2 py-1 border border-gray-300 rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 max-w-xs"
                             title="Add from favorites"
                           >
                             <option value="">
                               📚 Favorites ({favoriteSteps.length})
                             </option>
                             {favoriteSteps.map((favorite) => (
                               <option key={favorite.id} value={favorite.id}>
                                 {favorite.step_name} - {favorite.drill_name}
                               </option>
                             ))}
                           </select>
                         ) : (
                           <div className="px-2 py-1 border border-gray-300 rounded text-xs bg-gray-50 text-gray-500">
                             No favorites
                           </div>
                         )}
                       </div>
                     </div>

                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Step Name</label>
                      <input
                        type="text"
                        value={step.name}
                        onChange={(e) => updateStep(positionGroup.group_name, stepIndex, 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="e.g., WR: Route Combinations"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Setup</label>
                        <textarea
                          value={step.setup}
                          onChange={(e) => updateStep(positionGroup.group_name, stepIndex, 'setup', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md h-20"
                          placeholder="Equipment and setup instructions"
                        ></textarea>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Execution</label>
                        <textarea
                          value={step.execution}
                          onChange={(e) => updateStep(positionGroup.group_name, stepIndex, 'execution', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md h-20"
                          placeholder="How to execute the drill"
                        ></textarea>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Equipment</label>
                        <input
                          type="text"
                          value={step.equipment}
                          onChange={(e) => updateStep(positionGroup.group_name, stepIndex, 'equipment', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="Required equipment"
                        />
                      </div>
                      <div>
                        {/* Video URL field with Generate URL button */}
                        <div className="mb-3">
                          <label className="block text-sm font-medium mb-1">
                            Video URL
                          </label>
                          <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={step.video_url}
                          onChange={(e) => updateStep(positionGroup.group_name, stepIndex, 'video_url', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                              placeholder="Video URL"
                            />
                            <button
                              type="button"
                              onClick={() => handleGenerateVideoUrl(positionGroup.group_name, stepIndex)}
                              disabled={!step.name || !drillForm.name}
                              className={`px-3 py-2 rounded-md flex items-center gap-1 ${
                                !step.name || !drillForm.name
                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                  : 'bg-blue-600 text-white hover:bg-blue-700'
                              }`}
                              title={
                                !step.name || !drillForm.name
                                  ? "Fill out drill name and step name first"
                                  : "Search for videos related to this drill step"
                              }
                            >
                              <Search size={16} />
                              Find Videos
                            </button>
                          </div>
                          {!step.video_url && (
                            <p className="text-xs text-blue-600 mt-1">
                              A video URL will be automatically generated when you save the drill
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Coaching Points */}
                    <div className="mt-4 mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-gray-700">Coaching Points</label>
                        <button
                          type="button"
                          onClick={() => addCoachingPoint(positionGroupIndex, stepIndex)}
                          className="btn-primary btn-sm flex items-center gap-1"
                        >
                          <Plus size={14} />
                          Add Point
                        </button>
                      </div>

                      <div className="space-y-2">
                        {step.coaching_points.map((point, pointIndex) => {
                          const refKey = `${positionGroupIndex}-${stepIndex}-${pointIndex}`;
                          return (
                            <div key={pointIndex} className="flex items-start gap-2">
                              <input
                                type="text"
                                value={point}
                                onChange={(e) => updateCoachingPoint(positionGroup.group_name, stepIndex, pointIndex, e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                                placeholder="Enter coaching point"
                                ref={newCoachingPointRefs.current[refKey]}
                              />
                              <button
                                type="button"
                                onClick={() => removeCoachingPoint(positionGroupIndex, stepIndex, pointIndex)}
                                className="btn-danger btn-sm flex items-center gap-1"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}

                                 {/* Add New Step Button */}
                 <div className="flex justify-center mt-4">
                   <button
                     type="button"
                     onClick={() => addStep(positionGroupIndex)}
                     className="btn-primary flex items-center gap-2 px-4"
                   >
                     <Plus size={18} />
                     Add New Step
                   </button>
                 </div>
              </div>
            ))}
          </form>
        </div>
        
        <div className="p-3 flex-shrink-0 border-t bg-white flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            aria-label="Cancel drill form"
            className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            aria-label="Save drill"
            className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            {editingDrill ? 'Update Drill' : 'Add Drill'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DrillForm; 
